import { Link } from "@/lib/nav";

import FilterChips from "@/components/filter-chips";
import ListRowCard from "@/components/list-card";
import ListSearch from "@/components/list-search";
import Pager from "@/components/pager";
import ResponsiveList from "@/components/responsive-list";
import ListTable from "@buildpy/ui/list-table";
import {
  TableCell,
  TableRow,
} from "@buildpy/ui/table";
import Money from "@/components/money";
import { formatDate, formatDateTime } from "@buildpy/core/lib/format";
import { CONTRACT_STATUS_LABEL, CONTRACT_STATUSES } from "@buildpy/core/modules/contract/types";
import ContractStatusBadge from "@/modules/contract/ui/status-badge";
import { listHref } from "@buildpy/core/modules/list/search";
import type { ContractsView } from "@buildpy/core/views/g2a/contracts";
import PageHeader from "@/shell/page-header";

const Page = ({ data }: { data: ContractsView }) => {
  const { q, status, result, customers } = data;

  const contracts = result.rows;
  const customerName = new Map(customers.map((c) => [c.id, c.name]));

  // 칩·쪽 링크는 검색어를 싣고 간다 — 칩을 누르면 쪽은 1로.
  const href = (s: string | null, page?: number) => listHref("/app/contracts", { q, status: s, page });
  const chips = [
    { href: href(null), label: "전체", count: result.counts.ALL },
    ...CONTRACT_STATUSES.map((s) => ({
      href: href(s),
      label: CONTRACT_STATUS_LABEL[s],
      count: result.counts[s],
    })),
  ];

  const emptyText = q ? "검색어에 맞는 계약이 없습니다." : "계약이 없습니다. 수주한 견적의 상세에서 계약서를 만들 수 있습니다.";
  const nameOf = (contract: (typeof contracts)[number]) => customerName.get(contract.customer_id) ?? contract.client_name ?? "—";

  // 폰(md 미만)은 한 행 = 카드 한 장 — 표는 총액·상태 열이 화면 밖으로 밀렸다.
  const cards = contracts.map((contract) => (
    <ListRowCard
      key={contract.id}
      amount={<Money value={contract.total_amount} />}
      badge={<ContractStatusBadge status={contract.status} />}
      href={`/app/contracts/${contract.id}`}
      meta={[contract.contract_no, `수정 ${formatDate(contract.updated_at)}`]}
      subtitle={contract.site_address || "—"}
      testId={`contract-card-${contract.id}`}
      title={nameOf(contract)}
    />
  ));

  return (
    <>
      <PageHeader meta={`${result.total}건`} title="계약" />

      <div className="flex flex-col gap-y-3">
        <ListSearch
          action="/app/contracts"
          aria-label="계약 검색"
          keep={{ status }}
          nameMatchCapped={result.nameMatchCapped}
          placeholder="계약번호 · 공사명 · 고객 · 현장 주소"
          q={q}
        />
        <FilterChips activeHref={href(status)} aria-label="계약 상태" items={chips} />
      </div>

      <ResponsiveList
        cards={cards}
        empty={emptyText}
        label="계약 목록"
        testId="contract-cards"
        table={
          <ListTable
            columns={[
              { label: "계약번호" },
              { label: "고객" },
              { label: "현장", wrap: true },
              { label: "총액(부가세 포함)", align: "right" },
              { label: "상태" },
              { label: "최근 수정" },
            ]}
            empty={emptyText}>
            {contracts.map((contract) => (
              <TableRow key={contract.id} className="relative">
                <TableCell className="whitespace-nowrap px-3 py-2 font-medium">
                  <Link className="after:absolute after:inset-0" href={`/app/contracts/${contract.id}`}>
                    {contract.contract_no}
                  </Link>
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2">
                  {nameOf(contract)}
                </TableCell>
                <TableCell className="px-3 py-2">{contract.site_address || "—"}</TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                  <Money value={contract.total_amount} />
                </TableCell>
                <TableCell className="px-3 py-2">
                  <ContractStatusBadge status={contract.status} />
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                  {formatDateTime(contract.updated_at)}
                </TableCell>
              </TableRow>
            ))}
          </ListTable>
        }
      />

      <Pager hrefFor={(page) => href(status, page)} page={result.page} pageCount={result.pageCount} />
    </>
  );
};

export default Page;
